import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext'; // Assuming authContext is in the same directory

const LocationCoordinateContext = createContext();

export const useLocationCoordinate = () => {
  const context = useContext(LocationCoordinateContext);
  if (!context) {
    throw new Error('useLocationCoordinate must be used within a LocationCoordinateProvider');
  }
  return context;
};

export const LocationCoordinateProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // Define the base URL for the location coordinate service
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

  // Determine the correct API path based on the user's login type
  const getLocationBaseUrl = useCallback(() => {
    // This assumes your API gateway routes based on the service name, e.g., 'location-coordinate'
    const servicePath = 'location-coordinate/api/v1/locations';
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/${servicePath}`;
    }
    if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/${servicePath}`;
    }
    throw new Error('Invalid login type for location coordinate access');
  }, [API_BASE_URL, loginType]);

  // A generic helper to handle API responses and errors
  const handleApiResponse = async (response) => {
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || data.message || `Request failed with status ${response.status}`);
    }
    return data;
  };

  // Callback to clear the error state manually
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validates that the user has the necessary permissions to access the service
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access location data');
    }
  }, [loginType]);

  /**
   * Retrieves a full business location record by its ID.
   * Corresponds to: GET /api/v1/locations/{locationId}
   * @param {string} locationId - The unique ID of the business location.
   * @returns {Promise<object>} The full location data object.
   */
  const getLocationById = useCallback(async (locationId) => {
    validateAccess();
    if (!locationId) {
      throw new Error('Location ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getLocationBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/${encodeURIComponent(locationId)}`);
      return await handleApiResponse(response);
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch location data';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getLocationBaseUrl]);

  /**
   * Updates the coordinates for a specific business location.
   * Corresponds to: PATCH /api/v1/locations/{locationId}/coordinates
   * @param {string} locationId - The ID of the location to update.
   * @param {{latitude: number, longitude: number}} coordinates - The new coordinate data.
   * @returns {Promise<object>} The full, updated location data object.
   */
  const updateLocationCoordinates = useCallback(async (locationId, coordinates) => {
    validateAccess();
    if (!locationId) {
      throw new Error('Location ID is required');
    }
    if (!coordinates || typeof coordinates.latitude !== 'number' || typeof coordinates.longitude !== 'number') {
      throw new Error('Valid latitude and longitude are required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getLocationBaseUrl();
      const url = `${baseUrl}/${encodeURIComponent(locationId)}/coordinates`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          location_latitude: coordinates.latitude,
          location_longitude: coordinates.longitude,
        }),
      });

      return await handleApiResponse(response);
    } catch (err) {
      const errorMessage = err.message || 'Failed to update location coordinates';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getLocationBaseUrl]);

  /**
   * Checks the health of the location coordinate service.
   * @returns {Promise<object>} The health status object.
   */
  const checkHealth = useCallback(async () => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const healthUrl = (loginType === 'master'
        ? `${API_BASE_URL}/master/location-coordinate/health`
        : `${API_BASE_URL}/admin/location-coordinate/health`);
      
      const response = await makeAuthenticatedRequest(healthUrl);
      return await handleApiResponse(response);
    } catch (err) {
      const errorMessage = err.message || 'Health check failed for location service';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, API_BASE_URL, loginType]);
  
  /**
   * Validates coordinate values before sending them to the API.
   * @param {{latitude: number, longitude: number}} coords - The coordinates to validate.
   * @returns {Array<{field: string, message: string}>} An array of validation errors.
   */
  const validateCoordinates = useCallback((coords) => {
    const errors = [];
    if (coords.latitude < -90 || coords.latitude > 90) {
      errors.push({ field: 'latitude', message: 'Latitude must be between -90 and 90.' });
    }
    if (coords.longitude < -180 || coords.longitude > 180) {
      errors.push({ field: 'longitude', message: 'Longitude must be between -180 and 180.' });
    }
    return errors;
  }, []);

  /**
   * Formats a raw location data object for display purposes.
   * @param {object} location - The location data from the API.
   * @returns {object} The formatted location data with additional helpful fields.
   */
  const formatLocationData = useCallback((location) => {
    if (!location) return null;

    const fullAddress = [
      location.location_address_1,
      location.location_address_2,
      location.location_city,
      location.location_zip,
      location.location_country,
    ].filter(Boolean).join(', '); // Filter out null/empty parts and join

    return {
      ...location,
      fullAddress: fullAddress,
      googleMapsUrl: `https://www.google.com/maps/search/?api=1&query=${location.location_latitude},${location.location_longitude}`,
      visibilityText: location.location_visibility ? 'Visible' : 'Hidden',
      isMainLocation: location.location_main,
    };
  }, []);

  /**
   * Provides information about the current API endpoint configuration for debugging.
   * @returns {object} An object with details about the current endpoint.
   */
  const getEndpointInfo = useCallback(() => {
    return {
      loginType,
      baseUrl: getLocationBaseUrl(),
      healthUrl: (loginType === 'master'
        ? `${API_BASE_URL}/master/location-coordinate/health`
        : `${API_BASE_URL}/admin/location-coordinate/health`),
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
    };
  }, [loginType, API_BASE_URL, getLocationBaseUrl]);
  
  // The value object provided to consuming components
  const value = {
    // State
    loading,
    error,
    
    // Core API Actions
    getLocationById,
    updateLocationCoordinates,
    checkHealth,
    
    // Helpers and Utilities
    clearError,
    validateCoordinates,
    formatLocationData,
    getEndpointInfo,
    
    // Computed Boolean Values
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasLocationAccess: loginType === 'admin' || loginType === 'master',
  };

  return (
    <LocationCoordinateContext.Provider value={value}>
      {children}
    </LocationCoordinateContext.Provider>
  );
};